/**
 * CompanyLookupInput - Reusable input for resolving a company from a free-text query.
 *
 * Accepts a company name, website URL, or domain. Runs the two-step lookup pipeline:
 *   1. AI resolves the query to one or more candidate companies.
 *   2. If the match is definite (a clean domain/URL, or a single high-confidence
 *      result) → auto-enriches. Otherwise → prompts the user to pick one
 *      ("Did you mean A, B, or C?"), then enriches.
 *
 * The lookup runs on an explicit user action (button / Enter), or when the user
 * pastes a complete website / domain — dropping a URL in is unambiguous intent,
 * so the AI runs without a second click. It never fires while the user types, and
 * never on blur unless the caller opts in via `autoTriggerOnBlur`. Pasting a
 * company *name* does not trigger it either: the user may still want to edit it
 * before searching. This keeps the choice clear — the user either runs the AI
 * lookup, or skips it and enters details by hand (see `onSkip`).
 *
 * Emits the enrichment result + chosen domain via `onEnrichment`. The caller
 * decides what to do with it (e.g. fill an organization form, seed onboarding,
 * etc.). The component is presentation + flow only — no module-specific logic.
 *
 * Use sites:
 * - OrganizationQuickCreateDialog (Create Organization) — `framed` + `onSkip`
 * - OrganizationForm (full edit form) — inline
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClipboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Loader2, RotateCcw, Sparkles } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import { AIProcessingBanner } from './AIProcessingBanner';
import { ResponsiveDialog } from './ResponsiveDialog';
import { useCompanyLookup } from '../../hooks/useCompanyLookup';
import { isUrlLike } from '../../utils/validation';
import { valueAfterPaste } from '../../utils/urlPaste';
import type {
  CompanyCandidate,
  DomainEnrichmentResult,
} from '../../../modules/master_data/contacts/types';

export interface CompanyLookupInputProps {
  /** Free-text value (company name, website, or domain) */
  value: string;
  onChange: (value: string) => void;
  /** Called once enrichment completes; passes both the chosen domain and the enriched fields. */
  onEnrichment: (result: DomainEnrichmentResult, domain: string) => void;
  /** Trigger lookup automatically when the input loses focus (default: false — lookup is explicit) */
  autoTriggerOnBlur?: boolean;
  /** Optional async validator run before lookup (e.g. "domain already used"). Return an error message to abort. */
  preLookupCheck?: (cleanedQuery: string) => Promise<string | null> | string | null;
  /**
   * When provided, the control offers an explicit "skip" path: a "Skip — enter
   * details manually" link below the input, and a "None of these — enter
   * manually" escape inside the candidate picker. The caller typically uses this
   * to reveal/focus the manual fields.
   */
  onSkip?: () => void;
  /**
   * Called when the user picks a candidate that already exists in the tenant
   * (carries `existing_organization_id`). The caller should open that record
   * instead of creating a duplicate. When omitted, an existing-org candidate is
   * enriched like any other.
   */
  onSelectExisting?: (organizationId: string) => void;
  /**
   * Render the control as a self-contained "Auto-fill from the web" card (Sparkles
   * header + helper copy + bordered surface). Default false renders the bare input.
   */
  framed?: boolean;
  label?: string;
  placeholder?: string;
  hint?: string;
  error?: string | null;
  disabled?: boolean;
  id?: string;
}

function confidencePillClasses(confidence: CompanyCandidate['confidence']): string {
  switch (confidence) {
    case 'high':
      return 'bg-success/10 text-success';
    case 'low':
      return 'bg-warning/10 text-warning';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

export function CompanyLookupInput({
  value,
  onChange,
  onEnrichment,
  autoTriggerOnBlur = false,
  preLookupCheck,
  onSkip,
  onSelectExisting,
  framed = false,
  label,
  placeholder,
  hint,
  error,
  disabled,
  id = 'company-lookup',
}: CompanyLookupInputProps) {
  const { t } = useTranslation(['contacts', 'common']);
  const lookup = useCompanyLookup();
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastQueryRef = useRef<string>('');
  const [externalError, setExternalError] = useState<string | null>(null);

  // Surface enrichment to the caller exactly once per result.
  const lastSeenEnrichmentRef = useRef<DomainEnrichmentResult | null>(null);
  useEffect(() => {
    if (lookup.enrichment && lookup.enrichment !== lastSeenEnrichmentRef.current && lookup.selectedDomain) {
      lastSeenEnrichmentRef.current = lookup.enrichment;
      onEnrichment(lookup.enrichment, lookup.selectedDomain);
    }
  }, [lookup.enrichment, lookup.selectedDomain, onEnrichment]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const runLookup = useCallback(
    async (query: string) => {
      // Dedupe on the trimmed query so the paste path and a following blur can't
      // run the same lookup twice over a difference in surrounding whitespace.
      const trimmed = query.trim();
      if (!trimmed || trimmed === lastQueryRef.current) return;
      lastQueryRef.current = trimmed;

      if (preLookupCheck) {
        const err = await preLookupCheck(trimmed);
        if (err) {
          setExternalError(err);
          return;
        }
      }
      setExternalError(null);
      await lookup.lookup(query);
    },
    [lookup, preLookupCheck],
  );

  const handleBlur = useCallback(() => {
    if (!autoTriggerOnBlur || disabled) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (value.trim().length >= 2) runLookup(value);
    }, 300);
  }, [autoTriggerOnBlur, disabled, runLookup, value]);

  const handleManualTrigger = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (value.trim().length >= 2) runLookup(value);
  }, [runLookup, value]);

  // A pasted website/domain is unambiguous — look it up straight away. The
  // pending blur debounce is dropped so the same query can't run twice, and the
  // incoming value is passed explicitly because `value` still holds the
  // pre-paste text at this point.
  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      if (disabled || lookup.isLoading) return;
      const pastedValue = valueAfterPaste(event);
      if (!isUrlLike(pastedValue)) return;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      runLookup(pastedValue.trim());
    },
    [disabled, lookup.isLoading, runLookup],
  );

  // Bail out to manual entry. Reset any in-flight lookup first so a late result
  // can't fire onEnrichment and overwrite what the user is about to type.
  const handleSkip = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    lookup.reset();
    lastQueryRef.current = '';
    onSkip?.();
  }, [lookup, onSkip]);

  const handlePickCandidate = useCallback(
    (candidate: CompanyCandidate) => {
      // Already in the tenant → open it instead of enriching/creating a duplicate.
      if (candidate.existing_organization_id && onSelectExisting) {
        lookup.reset();
        onSelectExisting(candidate.existing_organization_id);
        return;
      }
      lookup.selectCandidate(candidate.domain);
    },
    [lookup, onSelectExisting],
  );

  const handleCancelPicker = useCallback(() => {
    lookup.reset();
  }, [lookup]);

  // "None of these / enter manually" from inside the picker: drop the lookup and
  // hand off to the caller's manual path (reset clears candidates + step).
  const handlePickerManual = useCallback(() => {
    lookup.reset();
    lastQueryRef.current = '';
    onSkip?.();
  }, [lookup, onSkip]);

  // Re-open the input after a confirmed match so the user can search again.
  const handleClearMatch = useCallback(() => {
    lookup.reset();
    lastQueryRef.current = '';
  }, [lookup]);

  const errorMessage =
    error ??
    externalError ??
    (lookup.step === 'error' && lookup.error
      ? lookup.error === 'NO_MATCHES'
        ? t('contacts:organizations.lookupNoMatches', 'No matches found. Try a different name or enter the website directly.')
        : t('contacts:organizations.enrichmentFailed')
      : null);

  const matched = lookup.step === 'done' && lookup.enrichment && lookup.selectedDomain;
  const matchedName = lookup.enrichment?.name || value;

  // Matched confirmation row — shown (framed only) once a definite match enriched.
  const matchedRow = matched && framed && (
    <div className="flex items-center gap-3 rounded-md border border-border bg-card p-3">
      <img
        src={`https://logo.clearbit.com/${lookup.selectedDomain}`}
        alt=""
        className="h-8 w-8 flex-shrink-0 rounded bg-muted object-contain"
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
        }}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-foreground">{matchedName}</div>
        <div className="truncate text-xs text-muted-foreground">{lookup.selectedDomain}</div>
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={handleClearMatch} className="flex-shrink-0 gap-1.5 touch:min-h-[44px]">
        <RotateCcw className="h-3.5 w-3.5" />
        {t('contacts:organizations.lookupChange', 'Change')}
      </Button>
    </div>
  );

  const inputRow = (
    <div className="flex gap-2">
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={handleBlur}
        onPaste={handlePaste}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleManualTrigger();
          }
        }}
        placeholder={placeholder ?? t('contacts:organizations.lookupPlaceholder', 'Company name, website, or domain')}
        disabled={disabled || lookup.isLoading}
        className={cn('flex-1', errorMessage && 'border-destructive')}
      />
      <Button
        type="button"
        variant={framed ? 'default' : 'outline'}
        size={framed ? 'default' : 'icon'}
        onClick={handleManualTrigger}
        disabled={disabled || lookup.isLoading || !value.trim()}
        title={t('contacts:organizations.lookupCompany', 'Look up company')}
        className={framed ? 'flex-shrink-0 gap-1.5' : undefined}
      >
        {lookup.isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
        {framed && !lookup.isLoading && <span>{t('contacts:organizations.lookupAction', 'Look up')}</span>}
      </Button>
    </div>
  );

  const loadingBanner = lookup.isLoading && (
    <AIProcessingBanner
      message={
        lookup.step === 'resolving'
          ? t('contacts:organizations.lookupResolving', 'Finding matching companies…')
          : lookup.progressMessage || t('common:aiProcessing.lookingUpDomain')
      }
    />
  );

  const messageRow = errorMessage ? (
    <p className="text-sm text-destructive">{errorMessage}</p>
  ) : hint ? (
    <p className="text-xs text-muted-foreground">{hint}</p>
  ) : null;

  const skipRow = onSkip && !matched && (
    <div className="flex justify-center pt-1">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleSkip}
        className="text-muted-foreground touch:min-h-[44px]"
      >
        {t('contacts:organizations.skipLookup', 'Skip — enter details manually')}
      </Button>
    </div>
  );

  const picker = (
    <ResponsiveDialog
      open={lookup.step === 'awaiting_choice'}
      onOpenChange={(open) => {
        if (!open) handleCancelPicker();
      }}
      title={
        lookup.candidates.length === 1
          ? t('contacts:organizations.lookupConfirmMatch', 'Is this the right {{organization}}?')
          : t('contacts:organizations.lookupPickMatch', 'Which {{organization}} did you mean?')
      }
      className="max-w-md"
    >
      <ul className="space-y-2 pb-2">
        {lookup.candidates.map((candidate) => {
          // Only treat it as "already added" when the caller can actually open
          // the record (onSelectExisting). Without that handler, picking the
          // candidate falls through to enrichment, so the "open existing" CTA
          // would be misleading — show it as a normal candidate instead.
          const isExisting = Boolean(candidate.existing_organization_id && onSelectExisting);
          return (
          <li key={candidate.domain}>
            <button
              type="button"
              onClick={() => handlePickCandidate(candidate)}
              className="flex w-full items-start gap-3 rounded-md border border-border bg-card p-3 text-left transition hover:-translate-y-0.5 hover:border-primary hover:shadow-sm"
            >
              <img
                src={`https://logo.clearbit.com/${candidate.domain}`}
                alt=""
                className="mt-0.5 h-8 w-8 flex-shrink-0 rounded bg-muted object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                }}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-foreground">{candidate.name}</span>
                  {isExisting ? (
                    <span className="flex flex-shrink-0 items-center gap-1 rounded-full bg-info/10 px-2 py-0.5 text-xs font-medium text-info">
                      <Building2 className="h-3 w-3" />
                      {t('contacts:organizations.lookupAlreadyAdded', 'Already added')}
                    </span>
                  ) : (
                    <span
                      className={cn(
                        'flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
                        confidencePillClasses(candidate.confidence),
                      )}
                    >
                      {t(`contacts:organizations.lookupConfidence.${candidate.confidence}`, candidate.confidence)}
                    </span>
                  )}
                </div>
                <div className="truncate text-xs text-muted-foreground">{candidate.domain}</div>
                {isExisting ? (
                  <p className="mt-1 text-xs text-info">
                    {t('contacts:organizations.lookupOpenExisting', 'Open this {{organization}} instead of creating a duplicate')}
                  </p>
                ) : candidate.description ? (
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{candidate.description}</p>
                ) : null}
              </div>
            </button>
          </li>
          );
        })}
      </ul>
      <div className="flex items-center justify-between pt-2">
        {onSkip ? (
          <Button type="button" variant="ghost" size="sm" onClick={handlePickerManual} className="touch:min-h-[44px]">
            {t('contacts:organizations.lookupNoneOfThese', 'None of these — enter manually')}
          </Button>
        ) : (
          <span />
        )}
        <Button type="button" variant="ghost" size="sm" onClick={handleCancelPicker} className="touch:min-h-[44px]">
          {t('common:app.cancel')}
        </Button>
      </div>
    </ResponsiveDialog>
  );

  if (framed) {
    return (
      <div className="space-y-3 rounded-lg border border-border bg-muted/40 p-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 flex-shrink-0 text-primary" />
          <span className="text-sm font-medium text-foreground">
            {label ?? t('contacts:organizations.autoFillTitle', 'Auto-fill from the web')}
          </span>
        </div>
        {matchedRow || (
          <>
            {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
            {inputRow}
            {loadingBanner}
            {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
            {skipRow}
          </>
        )}
        {picker}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {label && <Label htmlFor={id}>{label}</Label>}
      {inputRow}
      {loadingBanner}
      {messageRow}
      {skipRow}
      {picker}
    </div>
  );
}
